/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CreateImageUploadDto,
  CreatePersonaDto,
  CreateReferenceUploadDto,
  ImageUploadDto,
  PersonaDto,
  PersonaListDto,
  PersonaPostsDto,
  SetImageDto,
  UpdatePersonaDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The workspace's personas, oldest first, with the plan's allowance.
 */
export const listPersonas = (id: string, signal?: AbortSignal) => {
  return customInstance<PersonaListDto>({
    url: `/api/v1/workspaces/${id}/personas`,
    method: 'GET',
    signal,
  });
};

export const getListPersonasQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/personas`] as const;
};

export const getListPersonasQueryOptions = <
  TData = Awaited<ReturnType<typeof listPersonas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPersonasQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPersonas>>> = ({ signal }) =>
    listPersonas(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListPersonasQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPersonas>>
>;
export type ListPersonasQueryError = ErrorType<ApiErrorDto>;

export function useListPersonas<
  TData = Awaited<ReturnType<typeof listPersonas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPersonas>>,
          TError,
          Awaited<ReturnType<typeof listPersonas>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPersonas<
  TData = Awaited<ReturnType<typeof listPersonas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPersonas>>,
          TError,
          Awaited<ReturnType<typeof listPersonas>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPersonas<
  TData = Awaited<ReturnType<typeof listPersonas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's personas, oldest first, with the plan's allowance.
 */

export function useListPersonas<
  TData = Awaited<ReturnType<typeof listPersonas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonas>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPersonasQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add a persona. Owners and editors, within the plan's allowance.
 */
export const createPersona = (
  id: string,
  createPersonaDto: BodyType<CreatePersonaDto>,
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createPersonaDto,
    signal,
  });
};

export const getCreatePersonaMutationKey = () => ['createPersona'] as const;

export const getCreatePersonaMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPersona>>,
    TError,
    CreatePersonaMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPersona>>,
  TError,
  CreatePersonaMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePersonaMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPersona>>,
    CreatePersonaMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createPersona(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePersonaMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPersona>>
>;
export type CreatePersonaMutationBody = BodyType<CreatePersonaDto>;
export type CreatePersonaMutationError = ErrorType<ApiErrorDto>;
export type CreatePersonaMutationVariables = {
  id: string;
  data: BodyType<CreatePersonaDto>;
};

/**
 * @summary Add a persona. Owners and editors, within the plan's allowance.
 */
export const useCreatePersona = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPersona>>,
      TError,
      CreatePersonaMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPersona>>,
  TError,
  CreatePersonaMutationVariables,
  TContext
> => {
  return useMutation(getCreatePersonaMutationOptions(options), queryClient);
};
/**
 * @summary One persona, with its accounts and reference look.
 */
export const getPersona = (id: string, personaId: string, signal?: AbortSignal) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}`,
    method: 'GET',
    signal,
  });
};

export const getGetPersonaQueryKey = (id: string, personaId: string) => {
  return [`/api/v1/workspaces/${id}/personas/${personaId}`] as const;
};

export const getGetPersonaQueryOptions = <
  TData = Awaited<ReturnType<typeof getPersona>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPersonaQueryKey(id, personaId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPersona>>> = ({ signal }) =>
    getPersona(id, personaId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && personaId !== null && personaId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPersonaQueryResult = NonNullable<Awaited<ReturnType<typeof getPersona>>>;
export type GetPersonaQueryError = ErrorType<ApiErrorDto>;

export function useGetPersona<
  TData = Awaited<ReturnType<typeof getPersona>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPersona>>,
          TError,
          Awaited<ReturnType<typeof getPersona>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPersona<
  TData = Awaited<ReturnType<typeof getPersona>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPersona>>,
          TError,
          Awaited<ReturnType<typeof getPersona>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPersona<
  TData = Awaited<ReturnType<typeof getPersona>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary One persona, with its accounts and reference look.
 */

export function useGetPersona<
  TData = Awaited<ReturnType<typeof getPersona>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPersona>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPersonaQueryOptions(id, personaId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename a persona or change its short name or voice.
 */
export const updatePersona = (
  id: string,
  personaId: string,
  updatePersonaDto: BodyType<UpdatePersonaDto>,
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updatePersonaDto,
    signal,
  });
};

export const getUpdatePersonaMutationKey = () => ['updatePersona'] as const;

export const getUpdatePersonaMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePersona>>,
    TError,
    UpdatePersonaMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePersona>>,
  TError,
  UpdatePersonaMutationVariables,
  TContext
> => {
  const mutationKey = getUpdatePersonaMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePersona>>,
    UpdatePersonaMutationVariables
  > = (props) => {
    const { id, personaId, data } = props ?? {};

    return updatePersona(id, personaId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePersonaMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePersona>>
>;
export type UpdatePersonaMutationBody = BodyType<UpdatePersonaDto>;
export type UpdatePersonaMutationError = ErrorType<ApiErrorDto>;
export type UpdatePersonaMutationVariables = {
  id: string;
  personaId: string;
  data: BodyType<UpdatePersonaDto>;
};

/**
 * @summary Rename a persona or change its short name or voice.
 */
export const useUpdatePersona = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updatePersona>>,
      TError,
      UpdatePersonaMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updatePersona>>,
  TError,
  UpdatePersonaMutationVariables,
  TContext
> => {
  return useMutation(getUpdatePersonaMutationOptions(options), queryClient);
};
/**
 * @summary Delete a persona, with its social accounts.
 */
export const deletePersona = (id: string, personaId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeletePersonaMutationKey = () => ['deletePersona'] as const;

export const getDeletePersonaMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePersona>>,
    TError,
    DeletePersonaMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePersona>>,
  TError,
  DeletePersonaMutationVariables,
  TContext
> => {
  const mutationKey = getDeletePersonaMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePersona>>,
    DeletePersonaMutationVariables
  > = (props) => {
    const { id, personaId } = props ?? {};

    return deletePersona(id, personaId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePersonaMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePersona>>
>;

export type DeletePersonaMutationError = ErrorType<ApiErrorDto>;
export type DeletePersonaMutationVariables = { id: string; personaId: string };

/**
 * @summary Delete a persona, with its social accounts.
 */
export const useDeletePersona = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deletePersona>>,
      TError,
      DeletePersonaMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deletePersona>>,
  TError,
  DeletePersonaMutationVariables,
  TContext
> => {
  return useMutation(getDeletePersonaMutationOptions(options), queryClient);
};
/**
 * @summary Start an avatar upload: returns a short-lived URL to PUT the image to
(PNG, JPG, WebP or SVG, up to 2 MB), then call setPersonaAvatar with the key.
 */
export const createPersonaAvatarUpload = (
  id: string,
  personaId: string,
  createImageUploadDto: BodyType<CreateImageUploadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ImageUploadDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/avatar/upload`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createImageUploadDto,
    signal,
  });
};

export const getCreatePersonaAvatarUploadMutationKey = () =>
  ['createPersonaAvatarUpload'] as const;

export const getCreatePersonaAvatarUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPersonaAvatarUpload>>,
    TError,
    CreatePersonaAvatarUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPersonaAvatarUpload>>,
  TError,
  CreatePersonaAvatarUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePersonaAvatarUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPersonaAvatarUpload>>,
    CreatePersonaAvatarUploadMutationVariables
  > = (props) => {
    const { id, personaId, data } = props ?? {};

    return createPersonaAvatarUpload(id, personaId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePersonaAvatarUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPersonaAvatarUpload>>
>;
export type CreatePersonaAvatarUploadMutationBody = BodyType<CreateImageUploadDto>;
export type CreatePersonaAvatarUploadMutationError = ErrorType<ApiErrorDto>;
export type CreatePersonaAvatarUploadMutationVariables = {
  id: string;
  personaId: string;
  data: BodyType<CreateImageUploadDto>;
};

/**
 * @summary Start an avatar upload: returns a short-lived URL to PUT the image to
(PNG, JPG, WebP or SVG, up to 2 MB), then call setPersonaAvatar with the key.
 */
export const useCreatePersonaAvatarUpload = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPersonaAvatarUpload>>,
      TError,
      CreatePersonaAvatarUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPersonaAvatarUpload>>,
  TError,
  CreatePersonaAvatarUploadMutationVariables,
  TContext
> => {
  return useMutation(getCreatePersonaAvatarUploadMutationOptions(options), queryClient);
};
/**
 * @summary Use an uploaded image as the persona's avatar, replacing the previous one.
 */
export const setPersonaAvatar = (
  id: string,
  personaId: string,
  setImageDto: BodyType<SetImageDto>,
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/avatar`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: setImageDto,
    signal,
  });
};

export const getSetPersonaAvatarMutationKey = () => ['setPersonaAvatar'] as const;

export const getSetPersonaAvatarMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setPersonaAvatar>>,
    TError,
    SetPersonaAvatarMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setPersonaAvatar>>,
  TError,
  SetPersonaAvatarMutationVariables,
  TContext
> => {
  const mutationKey = getSetPersonaAvatarMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setPersonaAvatar>>,
    SetPersonaAvatarMutationVariables
  > = (props) => {
    const { id, personaId, data } = props ?? {};

    return setPersonaAvatar(id, personaId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetPersonaAvatarMutationResult = NonNullable<
  Awaited<ReturnType<typeof setPersonaAvatar>>
>;
export type SetPersonaAvatarMutationBody = BodyType<SetImageDto>;
export type SetPersonaAvatarMutationError = ErrorType<ApiErrorDto>;
export type SetPersonaAvatarMutationVariables = {
  id: string;
  personaId: string;
  data: BodyType<SetImageDto>;
};

/**
 * @summary Use an uploaded image as the persona's avatar, replacing the previous one.
 */
export const useSetPersonaAvatar = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setPersonaAvatar>>,
      TError,
      SetPersonaAvatarMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setPersonaAvatar>>,
  TError,
  SetPersonaAvatarMutationVariables,
  TContext
> => {
  return useMutation(getSetPersonaAvatarMutationOptions(options), queryClient);
};
/**
 * @summary Remove the persona's avatar.
 */
export const removePersonaAvatar = (
  id: string,
  personaId: string,
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/avatar`,
    method: 'DELETE',
    signal,
  });
};

export const getRemovePersonaAvatarMutationKey = () => ['removePersonaAvatar'] as const;

export const getRemovePersonaAvatarMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removePersonaAvatar>>,
    TError,
    RemovePersonaAvatarMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removePersonaAvatar>>,
  TError,
  RemovePersonaAvatarMutationVariables,
  TContext
> => {
  const mutationKey = getRemovePersonaAvatarMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removePersonaAvatar>>,
    RemovePersonaAvatarMutationVariables
  > = (props) => {
    const { id, personaId } = props ?? {};

    return removePersonaAvatar(id, personaId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemovePersonaAvatarMutationResult = NonNullable<
  Awaited<ReturnType<typeof removePersonaAvatar>>
>;

export type RemovePersonaAvatarMutationError = ErrorType<ApiErrorDto>;
export type RemovePersonaAvatarMutationVariables = { id: string; personaId: string };

/**
 * @summary Remove the persona's avatar.
 */
export const useRemovePersonaAvatar = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removePersonaAvatar>>,
      TError,
      RemovePersonaAvatarMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removePersonaAvatar>>,
  TError,
  RemovePersonaAvatarMutationVariables,
  TContext
> => {
  return useMutation(getRemovePersonaAvatarMutationOptions(options), queryClient);
};
/**
 * @summary Start a reference photo upload for one shot of the persona's reference
look: returns a short-lived URL to PUT the photo to (JPG, PNG or WebP,
up to 20 MB), then call setPersonaReference with the key.
 */
export const createPersonaReferenceUpload = (
  id: string,
  personaId: string,
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY',
  createReferenceUploadDto: BodyType<CreateReferenceUploadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ImageUploadDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/reference/${shot}/upload`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createReferenceUploadDto,
    signal,
  });
};

export const getCreatePersonaReferenceUploadMutationKey = () =>
  ['createPersonaReferenceUpload'] as const;

export const getCreatePersonaReferenceUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPersonaReferenceUpload>>,
    TError,
    CreatePersonaReferenceUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPersonaReferenceUpload>>,
  TError,
  CreatePersonaReferenceUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePersonaReferenceUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPersonaReferenceUpload>>,
    CreatePersonaReferenceUploadMutationVariables
  > = (props) => {
    const { id, personaId, shot, data } = props ?? {};

    return createPersonaReferenceUpload(id, personaId, shot, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePersonaReferenceUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPersonaReferenceUpload>>
>;
export type CreatePersonaReferenceUploadMutationBody = BodyType<CreateReferenceUploadDto>;
export type CreatePersonaReferenceUploadMutationError = ErrorType<ApiErrorDto>;
export type CreatePersonaReferenceUploadMutationVariables = {
  id: string;
  personaId: string;
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY';
  data: BodyType<CreateReferenceUploadDto>;
};

/**
 * @summary Start a reference photo upload for one shot of the persona's reference
look: returns a short-lived URL to PUT the photo to (JPG, PNG or WebP,
up to 20 MB), then call setPersonaReference with the key.
 */
export const useCreatePersonaReferenceUpload = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPersonaReferenceUpload>>,
      TError,
      CreatePersonaReferenceUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPersonaReferenceUpload>>,
  TError,
  CreatePersonaReferenceUploadMutationVariables,
  TContext
> => {
  return useMutation(
    getCreatePersonaReferenceUploadMutationOptions(options),
    queryClient,
  );
};
/**
 * @summary Use an uploaded photo for one shot of the reference look, replacing the previous one.
 */
export const setPersonaReference = (
  id: string,
  personaId: string,
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY',
  setImageDto: BodyType<SetImageDto>,
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/reference/${shot}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: setImageDto,
    signal,
  });
};

export const getSetPersonaReferenceMutationKey = () => ['setPersonaReference'] as const;

export const getSetPersonaReferenceMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setPersonaReference>>,
    TError,
    SetPersonaReferenceMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setPersonaReference>>,
  TError,
  SetPersonaReferenceMutationVariables,
  TContext
> => {
  const mutationKey = getSetPersonaReferenceMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setPersonaReference>>,
    SetPersonaReferenceMutationVariables
  > = (props) => {
    const { id, personaId, shot, data } = props ?? {};

    return setPersonaReference(id, personaId, shot, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetPersonaReferenceMutationResult = NonNullable<
  Awaited<ReturnType<typeof setPersonaReference>>
>;
export type SetPersonaReferenceMutationBody = BodyType<SetImageDto>;
export type SetPersonaReferenceMutationError = ErrorType<ApiErrorDto>;
export type SetPersonaReferenceMutationVariables = {
  id: string;
  personaId: string;
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY';
  data: BodyType<SetImageDto>;
};

/**
 * @summary Use an uploaded photo for one shot of the reference look, replacing the previous one.
 */
export const useSetPersonaReference = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setPersonaReference>>,
      TError,
      SetPersonaReferenceMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setPersonaReference>>,
  TError,
  SetPersonaReferenceMutationVariables,
  TContext
> => {
  return useMutation(getSetPersonaReferenceMutationOptions(options), queryClient);
};
/**
 * @summary Remove the photo for one shot of the reference look.
 */
export const removePersonaReference = (
  id: string,
  personaId: string,
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY',
  signal?: AbortSignal,
) => {
  return customInstance<PersonaDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/reference/${shot}`,
    method: 'DELETE',
    signal,
  });
};

export const getRemovePersonaReferenceMutationKey = () =>
  ['removePersonaReference'] as const;

export const getRemovePersonaReferenceMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removePersonaReference>>,
    TError,
    RemovePersonaReferenceMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removePersonaReference>>,
  TError,
  RemovePersonaReferenceMutationVariables,
  TContext
> => {
  const mutationKey = getRemovePersonaReferenceMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removePersonaReference>>,
    RemovePersonaReferenceMutationVariables
  > = (props) => {
    const { id, personaId, shot } = props ?? {};

    return removePersonaReference(id, personaId, shot);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemovePersonaReferenceMutationResult = NonNullable<
  Awaited<ReturnType<typeof removePersonaReference>>
>;

export type RemovePersonaReferenceMutationError = ErrorType<ApiErrorDto>;
export type RemovePersonaReferenceMutationVariables = {
  id: string;
  personaId: string;
  shot: 'FACE' | 'PROFILE' | 'BACK' | 'HALF_BODY' | 'FULL_BODY';
};

/**
 * @summary Remove the photo for one shot of the reference look.
 */
export const useRemovePersonaReference = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removePersonaReference>>,
      TError,
      RemovePersonaReferenceMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removePersonaReference>>,
  TError,
  RemovePersonaReferenceMutationVariables,
  TContext
> => {
  return useMutation(getRemovePersonaReferenceMutationOptions(options), queryClient);
};
/**
 * @summary Every post found on the persona's accounts, with its numbers on each platform, newest first.
 */
export const listPersonaPosts = (id: string, personaId: string, signal?: AbortSignal) => {
  return customInstance<PersonaPostsDto>({
    url: `/api/v1/workspaces/${id}/personas/${personaId}/posts`,
    method: 'GET',
    signal,
  });
};

export const getListPersonaPostsQueryKey = (id: string, personaId: string) => {
  return [`/api/v1/workspaces/${id}/personas/${personaId}/posts`] as const;
};

export const getListPersonaPostsQueryOptions = <
  TData = Awaited<ReturnType<typeof listPersonaPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPersonaPostsQueryKey(id, personaId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPersonaPosts>>> = ({
    signal,
  }) => listPersonaPosts(id, personaId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && personaId !== null && personaId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListPersonaPostsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPersonaPosts>>
>;
export type ListPersonaPostsQueryError = ErrorType<ApiErrorDto>;

export function useListPersonaPosts<
  TData = Awaited<ReturnType<typeof listPersonaPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPersonaPosts>>,
          TError,
          Awaited<ReturnType<typeof listPersonaPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPersonaPosts<
  TData = Awaited<ReturnType<typeof listPersonaPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPersonaPosts>>,
          TError,
          Awaited<ReturnType<typeof listPersonaPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPersonaPosts<
  TData = Awaited<ReturnType<typeof listPersonaPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Every post found on the persona's accounts, with its numbers on each platform, newest first.
 */

export function useListPersonaPosts<
  TData = Awaited<ReturnType<typeof listPersonaPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  personaId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPersonaPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPersonaPostsQueryOptions(id, personaId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
